import { useRef, useState } from "react";
import { api, API } from "../lib/api";
import { Button } from "./ui/button";
import { Upload, X, Loader2, Link as LinkIcon, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * Accepts an array of image URLs (or absolute /api/vehicle-photos/<id>.jpg paths)
 * and provides:
 *  - Drag-and-drop OR click-to-upload (multiple files)
 *  - Manual URL paste (one or more, comma-separated)
 *  - Per-image remove button
 *  - Reordering via drag (HTML5 native)
 *
 * Calls onChange with the new URL array.
 */
export function VehicleImageUploader({ value = [], onChange, vehicleId, testid = "image-uploader" }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [manualUrl, setManualUrl] = useState("");
  const fileInputRef = useRef(null);
  const dragIndex = useRef(null);

  const list = Array.isArray(value) ? value : (value ? String(value).split(",").map((s) => s.trim()).filter(Boolean) : []);

  const uploadFiles = async (files) => {
    const arr = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (arr.length === 0) {
      toast.error("Please pick image files (PNG, JPG, WEBP)");
      return;
    }
    setUploading(true);
    const uploaded = [];
    for (const file of arr) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        if (vehicleId) fd.append("vehicle_id", vehicleId);
        const { data } = await api.post("/vehicle-photos/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // Backend returns a relative URL like /api/vehicle-photos/<id>.jpg
        // Absolute it so it's portable across the inventory list
        const absolute = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        uploaded.push(absolute);
      } catch (err) {
        toast.error(`Upload failed for ${file.name}: ${err.response?.data?.detail || err.message}`);
      }
    }
    if (uploaded.length) {
      onChange([...list, ...uploaded]);
      toast.success(`Uploaded ${uploaded.length} image${uploaded.length === 1 ? "" : "s"}`);
    }
    setUploading(false);
  };

  const addManualUrls = () => {
    if (!manualUrl.trim()) return;
    const urls = manualUrl.split(",").map((s) => s.trim()).filter(Boolean);
    onChange([...list, ...urls]);
    setManualUrl("");
  };

  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));

  const onDragStart = (i) => { dragIndex.current = i; };
  const onDragOverItem = (e) => e.preventDefault();
  const onDropItem = (e, targetIdx) => {
    e.preventDefault();
    const src = dragIndex.current;
    if (src === null || src === targetIdx) return;
    const next = [...list];
    const [moved] = next.splice(src, 1);
    next.splice(targetIdx, 0, moved);
    onChange(next);
    dragIndex.current = null;
  };

  return (
    <div data-testid={testid} className="space-y-3">
      {/* Upload zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          uploadFiles(e.dataTransfer.files);
        }}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-md p-5 cursor-pointer transition text-center
          ${dragOver ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/15 hover:border-[#D4AF37]/60 bg-white/[0.02]"}`}
        data-testid="image-uploader-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => uploadFiles(e.target.files)}
          data-testid="image-uploader-input"
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-sm text-slate-300 py-3">
            <Loader2 className="w-5 h-5 animate-spin text-[#D4AF37]" />
            Uploading…
          </div>
        ) : (
          <>
            <Upload className="w-7 h-7 text-[#D4AF37] mx-auto mb-2" />
            <div className="text-sm text-white font-medium">Drag images here, or click to pick files</div>
            <div className="text-xs text-slate-400 mt-1">PNG, JPG, WEBP · multiple files OK · max 10 MB each</div>
          </>
        )}
      </div>

      {/* Manual URL paste (still supported) */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input
            type="text"
            placeholder="…or paste image URLs (comma-separated)"
            value={manualUrl}
            onChange={(e) => setManualUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManualUrls(); } }}
            className="w-full bg-[#030712] border border-white/10 rounded-sm h-9 pl-8 pr-3 text-xs text-white"
            data-testid="image-uploader-url-input"
          />
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addManualUrls} className="rounded-sm h-9 bg-transparent border-white/15 text-xs">
          Add URL
        </Button>
      </div>

      {/* Existing images */}
      {list.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 mt-2">
          {list.map((src, i) => (
            <div
              key={`${src}-${i}`}
              draggable
              onDragStart={() => onDragStart(i)}
              onDragOver={onDragOverItem}
              onDrop={(e) => onDropItem(e, i)}
              className="relative group aspect-square rounded-sm overflow-hidden bg-[#030712] border border-white/10 cursor-move"
              data-testid={`image-uploader-item-${i}`}
            >
              <img src={src} alt={`vehicle-${i + 1}`} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} />
              {i === 0 && (
                <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded-sm bg-[#D4AF37] text-black text-[9px] font-bold uppercase tracking-wider">Cover</div>
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); remove(i); }}
                className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                aria-label="Remove image"
                data-testid={`image-uploader-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {list.length === 0 && !uploading && (
        <div className="text-xs text-slate-500 flex items-center gap-2 pt-1">
          <ImageIcon className="w-3 h-3" /> No images yet. The first image becomes the cover.
        </div>
      )}
    </div>
  );
}
